import React from "react";
import type { Components } from "react-markdown";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const components: Components = {
  // 제목 - 명확한 계층 구조
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-gray-900 mt-4 mb-2 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[15px] font-bold text-gray-900 mt-3 mb-1.5 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-gray-900 mt-2.5 mb-1 first:mt-0">
      {children}
    </h3>
  ),

  // 단락
  p: ({ children }) => (
    <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>
  ),

  // 볼드
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),

  // 이탤릭
  em: ({ children }) => (
    <em className="italic text-gray-700">{children}</em>
  ),

  // 리스트 - Primary 마커 색상
  ul: ({ children }) => (
    <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-1 marker:text-blue-500">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-1 marker:text-blue-500">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed">{children}</li>
  ),

  // 코드 - 인라인 vs 블록 분기
  code: ({ className, children, ...props }) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <code
          className="block text-xs font-mono leading-relaxed"
          {...props}
        >
          {children}
        </code>
      );
    }
    // 인라인 코드 - 고정폭 폰트
    return (
      <code
        className="bg-gray-200 text-blue-700 px-1 py-0.5 rounded font-mono text-xs"
        {...props}
      >
        {children}
      </code>
    );
  },

  // 코드 블록 래퍼
  pre: ({ children }) => (
    <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg mb-2 last:mb-0 overflow-x-auto">
      {children}
    </pre>
  ),

  // 인용 - 주의사항 강조
  blockquote: ({ children }) => (
    <blockquote className="border-l-3 border-blue-500 bg-blue-50 pl-3 py-2 my-2 rounded-r-md text-sm text-gray-700 [&>p]:mb-0">
      {children}
    </blockquote>
  ),

  // 수평선
  hr: () => <hr className="border-gray-200 my-3" />,

  // 링크
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-700"
    >
      {children}
    </a>
  ),

  // 테이블 (GFM)
  table: ({ children }) => (
    <div className="overflow-x-auto mb-2 last:mb-0">
      <table className="min-w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-100">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-gray-200 px-2 py-1 text-left font-semibold text-gray-800">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-2 py-1 text-gray-700">
      {children}
    </td>
  ),
};

/**
 * AI 응답 마크다운 렌더러
 * - react-markdown 기반, XSS-safe
 * - 프로젝트 디자인 토큰에 맞는 Tailwind 스타일 적용
 * - 스트리밍 중 부분 마크다운도 graceful 렌더링
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = "",
}) => {
  return (
    <div className={`markdown-content text-sm leading-relaxed ${className}`}>
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </Markdown>
    </div>
  );
};

export default MarkdownRenderer;
